<template>
  <div class="result w-full bg-white">

    <!-- GENERAL INFO -->
    <div class="info-card px-4 py-6">
      <span class="bg-[#EBD4EF] text-[#9C27B0] text-xxs font-medium px-2 py-1 rounded border border-[#9C27B0] whitespace-nowrap">{{ lifeStageString }}</span>
      <h1 class="font-semibold text-2xl my-3">
        {{ userName }}
      </h1>
      <p class="text-base text-grey my-1">Tanggal Lahir &sdot; {{ formatDashDate(dob) }}</p>
      <p class="text-base text-grey my-1">NIK &sdot; {{ familyData.nikDisplay }}</p>

      <router-link :to="`/family-history?token=${encodeURIComponent(token)}`">
        <button class="w-full no-underline flex justify-between border-[1px] p-4 rounded-lg border-[#c3c3c3] my-3">
          <p class="text-base font-semibold">
            Riwayat Keluarga
          </p>
          <span class="icons flex flex-row items-center">
            <Circle class="w-6 h-6 mx-auto" :class="`bg-${familyHistoryStat}-dark`"/>
            <ChevronLeft class="rotate-180 w-6 h-6 ms-3" color="black"/>
          </span>
        </button>
      </router-link>

      <router-link
        v-if="lifeStageString === 'Bayi' || lifeStageString === 'Anak - anak'"
        :to="`/imunization?token=${encodeURIComponent(token)}&stage=${lifeStageCode}`"
      >
        <button class="w-full no-underline flex justify-between border-[1px] p-4 rounded-lg border-[#c3c3c3] my-3">
          <p class="text-base font-semibold">
            Riwayat Imunisasi
          </p>
          <span class="icons flex flex-row items-center">
            <Circle class="w-6 h-6 mx-auto" :class="`bg-${imunizationStat}-dark`"/>
            <ChevronLeft class="rotate-180 w-6 h-6 ms-3" color="black"/>
          </span>
        </button>
      </router-link>
    </div>

    <!-- RIWAYAT SURVEY -->

    <div id="survey-history-card" class="py-6 rounded-xl shadow-[rgba(0,0,15,0.1)_0px_-3px_3px_0px]">
      <div class="title flex justify-between px-4 items-center mb-5">
        <h2 class="font-semibold text-xl">Riwayat Survey</h2>
        <ChevronLeft class="rotate-90 w-6 h-6" color="black"/>
      </div>

      <div class="health-content w-full overflow-x-scroll">
        <TableHistory :current-data="screeningDataFull" :token="token"/>
      </div>
    </div>

    <Loading v-show="loading" class="top-0 left-0 bg-[rgba(0,0,0,0.25)]"/>
  </div>
</template>

<script setup>
import {
  computed, ref,
} from 'vue';

import Loading from '@/components/LoadingOverlay.vue';

import TableHistory from '@/components/tables/TableHistory.vue';
import ChevronLeft from '@/components/icons/ChevronLeft.vue';
import Circle from '@/components/icons/Circle.vue';

import { useStore } from 'vuex';
import { useRoute } from 'vue-router';

import { formatDashDate, getAgeMonths } from '@/utilities/helpers';
import {
  lifeStageType,
} from '@/utilities/constants';

const route = useRoute();
const store = useStore();
const token = ref(route?.query?.token || '');

const loading = computed(() => {
  const { loading } = store.state.global;
  return loading;
});

const userName = computed(() => {
  const { name } = store.state.global;
  return name;
});

const dob = computed(() => {
  const { dob } = store.state.global;
  return dob;
});

const familyData = computed(() => {
  const { familyData } = store.state.screening;
  return familyData;
});

const screeningDataFull = computed(() => {
  const { screeningData } = store.state.screening;
  return screeningData;
});

const lifeStageString = computed(() => lifeStageType(screeningDataFull.value?.[0]?.lifeStage, screeningDataFull.value?.[0]?.pregnancyStatus));

const lifeStageCode = computed(() => {
  const { lifeStage } = screeningDataFull.value?.[0];
  return lifeStage;
});

async function getFamilyData() {
  const res = await store.dispatch('screening/getFamilyData', {
    token: route.query.token,
    dob: store.state.global.dob,
  });
}
getFamilyData();

async function getHereditaryData() {
  const res = await store.dispatch('screening/getHereditaryDisease', {
    token: route.query.token,
    dob: store.state.global.dob,
  });
}
getHereditaryData();

const hereditary = computed(() => {
  const { hereditary } = store.state.screening;
  return hereditary;
});

const familyHistoryStat = computed(() => {
  let status = 'normal';
  const isDanger = Object.values(hereditary.value).filter((item) => item === true);
  if (isDanger.length === 1) status = 'warning';
  if (isDanger.length > 1) status = 'danger';
  return status;
});

async function getImunizationData() {
  const res = await store.dispatch('screening/getImunization', {
    token: route.query.token,
    dob: store.state.global.dob,
  });
  const master = await store.dispatch('screening/getMasterImunization', lifeStageCode.value);
}
getImunizationData();

const imunization = computed(() => {
  const { imunization } = store.state.screening;
  return imunization;
});

const masterImunization = computed(() => {
  const { masterImunization } = store.state.screening;
  return masterImunization;
});

const imunizationStat = computed(() => {
  let status = 'normal';
  if (lifeStageString.value === 'Bayi' || lifeStageString.value === 'Anak - anak') {
    const userAgeMonths = getAgeMonths(dob.value);
    let iterate = 0;
    Object.keys(masterImunization.value).every((key) => {
      if (masterImunization.value[key].months <= userAgeMonths) {
        iterate += 1;
        return true;
      }
      return false;
    });
    if (imunization.value?.length < iterate) status = 'warning';
  }
  return status;
});

</script>

<script>
export default {
  name: 'ResultPageView',
  methods: {
    scrollTo() {
      document.querySelector('.health-content').scrollTo(100, 0);
    },
  },
};
</script>
